import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMyTransactions } from '@shared/hooks/use-transactions';
import { Button } from '@/components/ui/button';

export function ShareTargetPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useMyTransactions({ pageSize: 50 });
  const [files, setFiles] = useState<File[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadSharedFiles() {
      try {
        const cache = await caches.open('share-target-files');
        const keys = await cache.keys();
        const found: File[] = [];
        for (const key of keys) {
          const res = await cache.match(key);
          if (res) {
            const blob = await res.blob();
            const fileName = decodeURIComponent(res.headers.get('x-filename') || 'shared-file');
            const type = res.headers.get('x-type') || 'application/octet-stream';
            const file = new File([blob], fileName, { type });
            found.push(file);
          }
        }
        setFiles(found);
      } catch (err: any) {
        console.error('Failed to load shared files:', err);
        setError('Failed to retrieve the shared files from cache.');
      } finally {
        setLoadingFiles(false);
      }
    }
    loadSharedFiles();
  }, []);

  // Format file size
  function formatBytes(bytes: number) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  function handleSelectTransaction(threadId: string) {
    // Simply navigate to the chat thread using history replacement so that
    // going back in history skips this transition share target page.
    navigate(`/client/chat/${threadId}`, { replace: true });
  }

  if (loadingFiles || isLoading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center p-6 text-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-200 border-t-brand-700"></div>
        <p className="mt-4 text-sm text-gray-600 font-medium">
          Retrieving shared files and active chats…
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
          ⚠️
        </div>
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Sharing Failed</h2>
        <p className="mt-2 text-sm text-gray-650">{error}</p>
        <Button className="mt-6" onClick={() => navigate('/client/home')}>
          Back to Home
        </Button>
      </div>
    );
  }

  if (files.length === 0) {
    return <Navigate to="/client/home" replace />;
  }

  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <header className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 shadow-sm">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
        </div>
        <h1 className="mt-4 text-xl font-bold text-gray-900 sm:text-2xl">Send Shared Files</h1>
        <p className="mt-1.5 text-sm text-gray-600">
          Select which active chat you want to send the shared file(s) to.
        </p>
      </header>

      {/* Shared file list */}
      <section className="mt-6 rounded-2xl border border-teal-100 bg-gradient-to-r from-brand-50/40 to-teal-50/20 p-4 shadow-sm">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-brand-800">
          Incoming Files
        </h2>
        <ul className="mt-3 divide-y divide-teal-100/40">
          {files.map((file, idx) => (
            <li key={idx} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white border border-teal-100/60 text-teal-700 shadow-sm font-semibold text-[10px] uppercase">
                {file.name.split('.').pop() || 'FILE'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900" title={file.name}>
                  {file.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatBytes(file.size)} • {file.type || 'Unknown Type'}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Destination Transactions list */}
      <section className="mt-8">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Attach to Active Chat
        </h2>

        {items.length === 0 ? (
          <div className="mt-3 rounded-xl border border-dashed border-gray-350 bg-gray-50 p-8 text-center">
            <p className="text-sm text-gray-600">
              You don't have any active transaction chats right now.
            </p>
            <Button className="mt-4 text-xs" onClick={() => navigate('/client/services')}>
              Browse Services to Start
            </Button>
          </div>
        ) : (
          <ul className="mt-3 space-y-3">
            {items.map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => handleSelectTransaction(t.id)}
                  className="flex w-full items-center justify-between rounded-xl border border-gray-250 bg-white p-4 text-left transition-all duration-200 hover:border-brand-300 hover:shadow-md hover:scale-[1.01] active:scale-[0.99] group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-xs font-semibold text-brand-700">
                      {t.service.name
                        .split(/\s+/)
                        .map((p) => p[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-brand-800">
                        {t.service.name}
                      </p>
                      <p className="text-xs text-gray-500">Code: #{t.code}</p>
                    </div>
                  </div>
                  <div className="ml-3 shrink-0 rounded-lg bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 group-hover:bg-brand-700 group-hover:text-white transition-colors duration-150">
                    Attach & Send
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
